import React from "react";

// ──── Shared Components ────

export const EmptyColumn = () => React.createElement(React.Fragment);

// ──── Shared Types ────

export type Tranche = {
    MinCommande: number;
    MaxCommande: number;
    FraisLivraison: number;
};
/**
 * Default VAT label pre-selected in the C&C forms (FR standard rate 20%).
 * Corresponds to a `value` of {@link VatRateRaw}, NOT to the backend `idTva`.
 */
export const defaultTvaLabel: string = "20";

/**
 * Returns {@link defaultTvaLabel} if the shop's VAT list contains it,
 * empty string otherwise. Single source of truth for the
 * "pre-fill TVA 20 if available" rule shared by the 4 C&C forms
 * (CityForm, CityAmountForm, ZoneForm, ZoneAmountForm).
 *
 * Use at form-reset time, NOT in a `useEffect` listening to `listTva`.
 */
export function resolveDefaultTva(vatList: VatRate[]): string {
    return vatList.some((v) => v.value === defaultTvaLabel)
        ? defaultTvaLabel
        : "";
}
export type SelectOption = {
    label: string;
    value: string | number;
};

export type PostalCodeOption = {
    label: string;
    value: string;
    postalLabel: string;
    code: string;
    pays: string;
};

// Shape returned by the backend — `value` and `id` come through as
// numbers despite the table column being labelled "Tva". We accept
// both so the type is honest, then normalise to strings everywhere
// downstream via getShopVatList / resolveTvaFromList.
export type VatRateRaw = {
    value: string | number;
    id: string | number;
};

// Normalised shape used everywhere inside the app — both fields are
// strings so `===` comparisons against form-state labels (which are
// always strings) work reliably.
export type VatRate = {
    value: string;
    id: string;
};

export type CountryOption = {
    label: string;
};

export type ZoneRecord = {
    DebutDistance: number;
    FinDistance: number;
    idTva: number;
    TauxTva: number;
    tiers: Tranche[];
};

export type CityRecord = {
    Department: string;
    CodePostale: string;
    NomVille: string;
    idTva: string | number;
    TauxTva: string | number;
    tiers: Tranche[];
    Pays: string;
    CodePays: string;
    SousVille: string;
};

export type ApiResponse<T = unknown> = {
    error: boolean;
    data: T;
};

export const INITIAL_TRANCHE: Tranche = {
    MinCommande: 0,
    MaxCommande: 0,
    FraisLivraison: 0,
};

export const NUMBER_REGEX = /^\d+([.,]\d{1,2})?$/;

// ──── Shared Utilities ────

export function scrollToFirstError(container?: HTMLElement | null) {
    setTimeout(() => {
        const target = (container || document).querySelector(".input__clz");
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
}

export const postalCodeFilterOption = (
    input: string,
    option?: PostalCodeOption
): boolean =>
    String(option?.label ?? "")
        .toLowerCase()
        .includes(input.toLowerCase());

export function removeLeadingZeros(number: string): number {
    const normalized = number.replace(",", ".");
    return parseFloat(normalized) || 0;
}

// A delivery distance range is invalid when the end distance is not
// strictly greater than the start distance. Covers (0,0), inverted
// ranges, and the equal-bounds case (e.g. 10 -> 10).
export function isInvalidDistanceRange(
    remoteDebut: number,
    endDistance: number
): boolean {
    return endDistance <= remoteDebut;
}

export type DayHours = {
    HeureDebut_Matin?: string | number;
    HeureFin_Matin?: string | number;
    HeureDebut_soir?: string | number;
    HeureFin_soir?: string | number;
};

export type DayOverlapFlags = {
    HeureDebut_Matin: boolean;
    HeureFin_Matin: boolean;
    HeureDebut_soir: boolean;
    HeureFin_soir: boolean;
};

// A schedule time field holds an "HHmm" value (e.g. "0830" -> 830). Returns the
// numeric value, or null when empty/unset. "HHmm" is monotonic within a day, so
// numeric comparison is a valid time comparison.
function toHhmmValue(value: unknown): number | null {
    if (value === "" || value === null || value === undefined) return null;
    return Number(String(value).replace(":", ""));
}

// Computes the overlap/validity flags for ONE day. A day has two slots —
// morning [HeureDebut_Matin, HeureFin_Matin] and evening [HeureDebut_soir,
// HeureFin_soir]. Within a slot the start must precede the end, and the two
// slots must not intersect. Flags the whole slot pair when a slot is invalid,
// and all four fields when the two slots overlap.
export function computeDayOverlapFlags(day: DayHours): DayOverlapFlags {
    const morningStart = toHhmmValue(day.HeureDebut_Matin);
    const morningEnd = toHhmmValue(day.HeureFin_Matin);
    const eveningStart = toHhmmValue(day.HeureDebut_soir);
    const eveningEnd = toHhmmValue(day.HeureFin_soir);

    const morningSet = morningStart !== null && morningEnd !== null;
    const eveningSet = eveningStart !== null && eveningEnd !== null;

    const morningInvalid = morningSet && morningStart! >= morningEnd!;
    const eveningInvalid = eveningSet && eveningStart! >= eveningEnd!;

    const slotsOverlap =
        morningSet &&
        eveningSet &&
        !morningInvalid &&
        !eveningInvalid &&
        morningStart! < eveningEnd! &&
        eveningStart! < morningEnd!;

    return {
        HeureDebut_Matin: morningInvalid || slotsOverlap,
        HeureFin_Matin: morningInvalid || slotsOverlap,
        HeureDebut_soir: eveningInvalid || slotsOverlap,
        HeureFin_soir: eveningInvalid || slotsOverlap,
    };
}

// Single source of truth for VAT lookup: resolves both idTva and
// tauxTva from the shop's per-shop list (selectedRowShops[0]["list Of
// VatRate"]) given the rate label selected in the form. Returns null
// when the label is not in the list — callers preserve their existing
// fallback behaviour.
export function resolveTvaFromList(
    tvaLabel: string,
    vatList: VatRate[] | undefined | null
): { idTva: string; tauxTva: string } | null {
    // Compare as strings: the list values are string-coerced (getShopVatList),
    // but `tvaLabel` may arrive as a number when prefilled straight from the
    // row data (e.g. CityForm stores the raw `Tva`, which the API may send as
    // a number). Strict `===` would then miss a valid rate (e.g. 20 vs "20").
    const row = (vatList || []).find(
        (el) => String(el.value) === String(tvaLabel)
    );
    if (!row) return null;
    return {
        idTva: row.id,
        tauxTva: row.value,
    };
}

export function getShopVatList(
    selectedRowShops: Record<string, unknown>[] | undefined
): VatRate[] {
    const raw =
        (selectedRowShops?.[0]?.["list Of VatRate"] as VatRateRaw[]) || [];
    return raw.map((el) => ({
        value: String(el.value),
        id: String(el.id),
    }));
}

export function convertToRealNumber(integer: string | number): string {
    return parseFloat(String(integer)).toFixed(2);
}

// ──── Shared API Helpers ────

export const fetchPays = async (
    pays: CountryOption[],
    setCountryList: (list: CountryOption[]) => void
) => {
    try {
        setCountryList(pays.map((el) => ({ label: el.label })));
    } catch (err) {
        throw Error("Failed to load country or VAT data", { cause: err });
    }
};

export const CountryPostalCodes = async (
    countryData: string,
    setCode: (list: PostalCodeOption[]) => void,
    i18nextLng: string,
    fetchGlobal: <T>(url: string, options?: RequestInit) => Promise<T>
) => {
    if (countryData === "") return;
    const apiUrl = `${
        process.env.REACT_APP_API_V2_URL
    }/settings/code/pays?pays=${encodeURIComponent(countryData)}`;

    try {
        const data = await fetchGlobal<
            ApiResponse<
                {
                    code_post: string;
                    nom_commune: string;
                    pays: string;
                    nom_fr_fr: string;
                }[]
            >
        >(apiUrl, {
            headers: {
                "Content-Type": "application/json",
                "Accept-Language": i18nextLng,
            },
            method: "GET",
        });

        if (data.error) {
            throw Error(`Error to load postal code of ${countryData} ! `, {
                cause: data.error,
            });
        }
        const seen = new Set<string>();
        const options: PostalCodeOption[] = [];
        for (const el of data.data) {
            const key = `${el.code_post}_${el.nom_commune}`;
            if (seen.has(key)) continue;
            seen.add(key);
            options.push({
                label: `${el.code_post} - ${el.nom_commune}`,
                value: key,
                postalLabel: el.code_post,
                code: el.pays,
                pays: el.nom_fr_fr,
            });
        }
        setCode(options);
    } catch (err) {
        throw Error("Failed to load postal codes", { cause: err });
    }
};
